'use client';

import * as React from 'react';
import {
  ChevronLeft,
  CheckCircle2,
  XCircle,
  Clock,
  PlayCircle,
  RotateCcw,
  AlertTriangle,
  ChevronDown,
  Loader2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useOperationStatus, useRollbackOperation } from '@/lib/api/subscriptionManager';
import { cn } from "@/lib/utils";

interface OperationDetailProps {
  operationId: string;
  onBack: () => void;
}

const ACTION_LABELS: Record<string, string> = {
  processor_change: 'Change Processor',
  billing_update: 'Update Billing',
  distribute_rebills: 'Distribute Rebills',
  pause: 'Pause',
  resume: 'Resume',
  cancel: 'Cancel',
  retry_new_processor: 'Retry Processor',
};

// Same split as the history list: `retry_new_processor` is two operations, and only the retry mode
// tells them apart. Naming it "Retry Processor" here would rename the operation between the row the
// merchant clicked and the panel that opens.
const labelFor = (op: { type: string; retryMode?: string }) =>
  op.retryMode === 'charge_now' ? 'Retry Payment' : ACTION_LABELS[op.type] || op.type;

const STATUS_CONFIG: Record<string, { icon: React.ElementType; color: string; bg: string; label: string }> = {
  pending: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Pending' },
  running: { icon: PlayCircle, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Running' },
  completed: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-100', label: 'Completed' },
  partially_completed: { icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-100', label: 'Partially Completed' },
  failed: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Failed' },
  rolled_back: { icon: RotateCcw, color: 'text-gray-600', bg: 'bg-gray-100', label: 'Rolled Back' },
};

function formatDateTime(date: Date) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(date));
}

// Set by the operation executor when a rebill ended in a cancellation rather than a decline. The
// per-item contract only has success/failed/skipped, so the distinction rides on the reason.
const CANCELLED_PREFIX = 'subscription_cancelled:';

export function OperationDetail({ operationId, onBack }: OperationDetailProps) {
  const { data: operation, isLoading } = useOperationStatus(operationId);
  const rollbackMutation = useRollbackOperation();

  const [successOpen, setSuccessOpen] = React.useState(false);
  const [failedOpen, setFailedOpen] = React.useState(true);
  const [skippedOpen, setSkippedOpen] = React.useState(false);

  const handleRollback = async () => {
    if (!operation?.canRollback) return;
    await rollbackMutation.mutateAsync({ operationId });
    onBack();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
        <div className="space-y-4">
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-40 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (!operation) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-muted-foreground">Operation not found</p>
        </div>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[operation.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  const successResults = operation.results?.filter((r) => r.status === 'success') || [];
  const failedResults = operation.results?.filter((r) => r.status === 'failed') || [];
  const skippedResults = operation.results?.filter((r) => r.status === 'skipped') || [];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
          <h2 className="text-lg font-semibold">Operation Details</h2>
        </div>
        {operation.canRollback && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleRollback}
            disabled={rollbackMutation.isPending}
          >
            {rollbackMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            <RotateCcw className="h-4 w-4 mr-1" />
            Rollback
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1 -mx-1 px-1">
        {/* Summary Card */}
        <div className="rounded-lg border bg-card p-4 mb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={cn('p-2 rounded-full', statusConfig.bg)}>
                <StatusIcon className={cn('h-5 w-5', statusConfig.color)} />
              </div>
              <div>
                <div className="font-medium">{labelFor(operation)}</div>
                <Badge variant="secondary" className="mt-1">
                  {statusConfig.label}
                </Badge>
              </div>
            </div>
            <code className="text-xs text-muted-foreground">{operationId}</code>
          </div>

          <Separator className="my-4" />

          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold">{operation.targetCount}</div>
              <div className="text-xs text-muted-foreground">Target</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{operation.successCount}</div>
              <div className="text-xs text-muted-foreground">Success</div>
            </div>
            <div>
              <div className={cn('text-2xl font-bold', operation.failedCount > 0 ? 'text-red-600' : '')}>
                {operation.failedCount}
              </div>
              <div className="text-xs text-muted-foreground">Failed</div>
            </div>
            <div>
              <div className={cn('text-2xl font-bold', operation.skippedCount > 0 ? 'text-yellow-600' : '')}>
                {operation.skippedCount}
              </div>
              <div className="text-xs text-muted-foreground">Skipped</div>
            </div>
          </div>

          <Separator className="my-4" />

          {/* Timestamps */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground">Started</div>
              <div>{formatDateTime(operation.createdAt)}</div>
            </div>
            {operation.completedAt && (
              <div>
                <div className="text-muted-foreground">Completed</div>
                <div>{formatDateTime(operation.completedAt)}</div>
              </div>
            )}
          </div>
        </div>

        {/* Results Sections */}
        <div className="space-y-3">
          {/* Success */}
          {successResults.length > 0 && (
            <Collapsible open={successOpen} onOpenChange={setSuccessOpen}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg border bg-green-50 hover:bg-green-100 transition-colors">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="font-medium text-green-800">
                    Success ({successResults.length})
                  </span>
                </div>
                <ChevronDown className={cn('h-4 w-4 text-green-600 transition-transform', successOpen && 'rotate-180')} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border bg-card p-2 space-y-1">
                  {successResults.map((result) => (
                    <div key={result.subscriptionId} className="text-xs py-1 px-2 rounded hover:bg-muted">
                      <code className="text-muted-foreground">{result.subscriptionId}</code>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Failed */}
          {failedResults.length > 0 && (
            <Collapsible open={failedOpen} onOpenChange={setFailedOpen}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg border bg-red-50 hover:bg-red-100 transition-colors">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <span className="font-medium text-red-800">
                    Failed ({failedResults.length})
                  </span>
                </div>
                <ChevronDown className={cn('h-4 w-4 text-red-600 transition-transform', failedOpen && 'rotate-180')} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border bg-card p-2 space-y-2">
                  {failedResults.map((result) => (
                    <div key={result.subscriptionId} className="text-xs py-1.5 px-2 rounded bg-red-50">
                      <code className="text-muted-foreground">{result.subscriptionId}</code>
                      {result.error && (
                        <div className="text-red-600 mt-0.5">
                          {result.error.startsWith(CANCELLED_PREFIX)
                            ? result.error.slice(CANCELLED_PREFIX.length)
                            : result.error}
                        </div>
                      )}
                      {result.error?.startsWith(CANCELLED_PREFIX) && (
                        <div className="text-red-700 font-medium mt-0.5">
                          This subscription was cancelled, not just declined.
                        </div>
                      )}
                      {result.failureClass === 'platform' && (
                        <div className="text-muted-foreground mt-0.5">
                          Never reached the bank — a problem on our side, not the cardholder&apos;s.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Skipped */}
          {skippedResults.length > 0 && (
            <Collapsible open={skippedOpen} onOpenChange={setSkippedOpen}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg border bg-yellow-50 hover:bg-yellow-100 transition-colors">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  <span className="font-medium text-yellow-800">
                    Skipped ({skippedResults.length})
                  </span>
                </div>
                <ChevronDown className={cn('h-4 w-4 text-yellow-600 transition-transform', skippedOpen && 'rotate-180')} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border bg-card p-2 space-y-2">
                  {skippedResults.map((result) => (
                    <div key={result.subscriptionId} className="text-xs py-1.5 px-2 rounded bg-yellow-50">
                      <code className="text-muted-foreground">{result.subscriptionId}</code>
                      {result.skipReason && (
                        <div className="text-yellow-700 mt-0.5">{result.skipReason}</div>
                      )}
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
